import { useRef, useState, type ReactNode } from "react";
import {
  SquareCheckBig,
  Loader2,
  Map as MapIcon,
  Settings2,
  X,
} from "lucide-react";
import {
  useFieldCovariates,
  useFieldLayerList,
  useHaneyPoints,
  useSoilComposition,
  useSoilSamples,
} from "../hooks/queries";
import { layersFromDocument } from "../lib/layerLoad";
import { useUiStore } from "../stores/uiStore";
import { useRxIntro } from "../stores/mapIntroStore";
import { useUiMode, showsAdvancedControls } from "../stores/uiModeStore";
import { useEnhancedVisuals } from "../stores/enhancedVisualsStore";
import { useRxEdit } from "../stores/rxEditStore";
import {
  computeAndSavePresetRec,
  fetchPresetInputsFor,
  presetNeedsSeasons,
  K_PRESETS,
  LIME_PRESETS,
  P_PRESETS,
  type FertPreset,
  type PresetInputs,
} from "../lib/fertPreset";
import { yieldOptionsFromCovariates, type Season } from "../lib/replacement";
import { BuildRxPreviewMap } from "./BuildRxPreviewMap";
import { SeasonsStep } from "../features/seasons-step/SeasonsStep";
import { useAutoRxLayer } from "../hooks/useAutoRxLayer";
import type { DashboardField, SoilSamplePoint } from "../types/contract";
import { Modal } from "../ui/Modal";

// Clicking a card runs the SAME preset workflow as before: a seasons/crops/yield pop-up (only for
// crop-removal presets), then computes + saves that nutrient's rec with the preset's exact flag
// combo (lib/fertPreset).

type View = "menu" | "seasons";
type TabKey = "lime" | "p2o5" | "k2o" | "nitrogen" | "seeding";

// "Our Favorite" preset per nutrient: Genius Rx for P & K, Traditional for Lime.
const FAVORITE_PRESET_IDS = new Set([
  "p-genius",
  "k-genius",
  "lime-traditional",
]);

export function BuildRxPanel({
  fields,
  onClose,
}: {
  fields: DashboardField[];
  onClose: () => void;
}) {
  const field = fields[0];
  const openMap = useUiStore((s) => s.openMap);
  const { data: listed, isLoading: lLayers } = useFieldLayerList(
    field.field_id,
  );
  const manifest = listed ? layersFromDocument(listed.document) : undefined;
  const enhancedVisuals = useEnhancedVisuals((s) => s.enabled);
  const { data: samples, isLoading: lSamples } = useSoilSamples(field.field_id);
  const { data: covs, isLoading: lCovs } = useFieldCovariates(field.field_id);
  const { data: comp, isLoading: lComp } = useSoilComposition(field.field_id);
  const { data: haneyPoints, isLoading: lHaney } = useHaneyPoints(
    field.field_id,
  );
  // Don't run a preset until the field's data has loaded — otherwise an available flag (e.g. K
  // Genius's cation balance) would be silently SKIPPED just because samples/clay/Haney hadn't
  // arrived yet, and the "skipped" notice would be wrong — rule: data persists server-side.
  const loadingData = lLayers || lSamples || lCovs || lComp || lHaney;

  const [view, setView] = useState<View>("menu");
  const [tab, setTab] = useState<TabKey>("p2o5");
  const [pending, setPending] = useState<FertPreset | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    msg: string;
    skipped: string[];
  } | null>(null);
  const runningRef = useRef(false); // hard guard against a double-dispatch (double-click / click-while-busy)

  const multi = fields.length > 1;
  const inputs: PresetInputs = {
    field,
    manifest: manifest ?? [],
    samples: samples ?? [],
    covs: covs ?? [],
    comp: comp ?? null,
    haneyPoints: haneyPoints ?? [],
  };
  // AutoRx previews render only when the field was mapped from REAL/uploaded samples: modeled maps
  // exist AND at least one sample carries lab results (`properties`). Otherwise the cards show a
  // "map with real samples" placeholder — a modeled-only map has no measured values to base a real
  // Rx on.
  const hasRealSamples =
    !!field.has_ml_map && (samples ?? []).some((s) => s.properties != null);

  async function run(preset: FertPreset, seasons: Season[]) {
    if (runningRef.current) return; // a compute is already in flight — never start a second
    runningRef.current = true;
    setBusy(true);
    setResult(null);
    try {
      // MULTI-FIELD: build + save this preset's Rx for EVERY selected field.
      if (multi) {
        const done: {
          field: DashboardField;
          ok: boolean;
          error?: string;
          skipped: string[];
        }[] = [];
        for (const f of fields) {
          const inp =
            f.field_id === field.field_id
              ? inputs
              : await fetchPresetInputsFor(f);
          const r = await computeAndSavePresetRec(
            inp,
            preset,
            seasons,
            preset.label,
          );
          done.push({ field: f, ok: r.ok, error: r.error, skipped: r.skipped });
        }
        setView("menu");
        setPending(null);
        const failed = done.filter((d) => !d.ok);
        const skipped = [...new Set(done.flatMap((d) => d.skipped))];
        if (failed.length) {
          setResult({
            ok: false,
            msg: `Built “${preset.label}” for ${done.length - failed.length} of ${done.length} fields. Failed: ${failed.map((d) => d.field.field_name).join(", ")} — ${failed[0].error ?? "error"}`,
            skipped: [],
          });
          return;
        }
        setResult({
          ok: true,
          msg: `Saved “${preset.label}” for all ${fields.length} fields.${skipped.length ? " Some options were skipped where data was missing:" : ""}`,
          skipped,
        });
        return;
      }
      const r = await computeAndSavePresetRec(
        inputs,
        preset,
        seasons,
        preset.label,
      );
      // clean run → straight to the map
      if (r.ok && r.skipped.length === 0) {
        openMap(field.field_id);
        onClose();
        return;
      }
      // otherwise (error, or saved-but-some-options-skipped) return to the MENU so the notice is
      // actually visible — `result` only renders in the menu view, not the seasons/custom modals.
      setView("menu");
      setPending(null);
      if (!r.ok) {
        setResult({
          ok: false,
          msg: r.error ?? "Something went wrong.",
          skipped: r.skipped,
        });
        return;
      }
      setResult({
        ok: true,
        msg: `Saved “${preset.label}”. Some options were skipped (data not available):`,
        skipped: r.skipped,
      });
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }

  // Customize -> the map, same as Map Soil: the panel closes, the field opens, and the editor takes over
  // with THIS preset already computed. Nothing is saved until the user says so.
  function customize(preset: FertPreset) {
    if (busy || loadingData || !inputs) return;
    onClose();
    // Widening it to every card would show one method's story over another's, so it is pinned to
    // the id rather than to the nutrient.
    if (enhancedVisuals && preset.id === "lime-traditional")
      useRxIntro.getState().arm(field.field_id);
    useUiStore.getState().openMap(field.field_id);
    // The session edits the first field. Save copies this spec onto every
    // selected field, each against its own grid.
    void useRxEdit
      .getState()
      .begin(inputs, preset, field.field_name ?? field.field_id, fields);
  }

  function pick(preset: FertPreset) {
    if (busy || loadingData) return; // don't act mid-compute or before the field's data has loaded
    setResult(null);
    if (presetNeedsSeasons(preset)) {
      setPending(preset);
      setView("seasons");
      return;
    }
    void run(preset, []); // lime/albrecht presets have no crop-removal step
  }

  const backdrop = (
    children: ReactNode,
    onBackdrop: () => void,
    width = "max-w-lg",
  ) => (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
        onClick={onBackdrop}
      >
        <div
          className={`flex max-h-[85vh] w-full ${width} flex-col rounded-lg bg-surface text-fg shadow-2xl`}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </Modal>
  );

  if (view === "seasons" && pending) {
    return backdrop(
      <SeasonsStep
        field={field}
        yieldOpts={yieldOptionsFromCovariates(covs ?? [])}
        preset={pending}
        busy={busy}
        onCancel={() => {
          setView("menu");
          setPending(null);
        }}
        onConfirm={(seasons) => void run(pending, seasons)}
      />,
      () => {
        if (!busy) {
          setView("menu");
          setPending(null);
        }
      },
      "max-w-md",
    );
  }

  // the inline nutrient-tabbed preset panel (menu view) tab order left→right: Phosphorus
  // Potassium, Lime, Nitrogen, Seeding.
  const tabs: {
    key: TabKey;
    label: string;
    presets?: FertPreset[];
    soon?: boolean;
  }[] = [
    { key: "p2o5", label: "Phosphorus", presets: P_PRESETS },
    { key: "k2o", label: "Potassium", presets: K_PRESETS },
    { key: "lime", label: "Lime", presets: LIME_PRESETS },
    { key: "nitrogen", label: "Nitrogen", soon: true },
    { key: "seeding", label: "Seeding", soon: true },
  ];
  const active = tabs.find((t) => t.key === tab)!;
  return (
    <div className="mt-2 flex flex-col overflow-hidden rounded-lg border border-line bg-surface text-fg shadow-md">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-3 py-1.5">
        <h2 className="text-[13px] font-semibold">
          Build Rx{" "}
          <span className="font-normal text-muted">— {field.field_name}</span>
        </h2>
        {multi && (
          <span className="text-[11px] text-faint">
            showing the first of {fields.length} fields — the preset you pick
            builds for all {fields.length}
          </span>
        )}
        {loadingData && !busy && (
          <span className="flex items-center gap-1 text-[11px] text-muted">
            <Loader2 size={11} className="animate-spin text-accent" /> loading
            the field’s data…
          </span>
        )}
        {busy && (
          <span className="flex items-center gap-1 text-[11px] text-muted">
            <Loader2 size={11} className="animate-spin text-accent" /> building
            the recommendation…
          </span>
        )}
        {result && (
          <span
            className={`text-[11px] ${result.ok ? "text-muted" : "text-red-400"}`}
          >
            {result.msg}
            {result.ok && (
              <button
                className="ml-1 inline-flex items-center gap-1 text-fg underline hover:no-underline"
                onClick={() => {
                  openMap(field.field_id);
                  onClose();
                }}
              >
                <MapIcon size={11} /> View on map
              </button>
            )}
          </span>
        )}
        <button
          onClick={onClose}
          className="ml-auto rounded p-1 hover:bg-hover"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* nutrient tabs */}
      <div
        className="flex flex-wrap justify-center gap-1 border-b border-line px-3 pt-1.5"
        role="tablist"
        aria-label="Nutrient"
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1 rounded-t border-b-2 px-3 py-1 text-[12px] ${tab === t.key ? "border-accent font-medium text-fg" : "border-transparent text-muted hover:text-fg"}`}
          >
            {t.label}
            {t.soon && (
              <span className="rounded bg-hover px-1 text-[8px] uppercase tracking-wide text-faint">
                soon
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Preset cards for the active nutrient. */}
      <div
        className="overflow-auto p-3"
        style={{ maxHeight: "min(460px, 60vh)" }}
      >
        <h1 className="mb-3 text-center text-lg font-bold text-fg sm:text-xl">
          How Do You Want to Build {active.label} Rx?
        </h1>
        {!field.has_ml_map ? (
          <p className="px-2 py-6 text-center text-[12px] text-muted">
            Build Rx needs the modeled soil-property maps first. Run{" "}
            <span className="font-medium text-fg">Map Soil</span> on this field,
            then come back.
          </p>
        ) : active.soon ? (
          <div className="flex h-40 items-center justify-center text-[12px] text-muted">
            {active.label} recs — coming soon
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {/* Custom is NOT a card — it lives in the footer as "full manual control". */}
            {(active.presets ?? [])
              .filter((p) => !p.custom)
              .map((p) => (
                <BuildRxCard
                  key={p.id}
                  preset={p}
                  onPick={pick}
                  onCustomize={customize}
                  disabled={busy || loadingData}
                  favorite={FAVORITE_PRESET_IDS.has(p.id)}
                  field={field}
                  inputs={inputs}
                  samples={samples ?? []}
                  showPreview={hasRealSamples}
                  inputsReady={!loadingData}
                />
              ))}
          </div>
        )}
        {result && result.skipped.length > 0 && (
          <ul className="ml-3 mt-2 list-disc text-[11px] text-faint">
            {result.skipped.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// One preset "card": an AutoRx preview map (the default Rx surface for the preset, with sampling
// points on top — ) + a description box; clicking the card runs that preset's workflow.
function BuildRxCard({
  preset,
  onPick,
  onCustomize,
  disabled,
  favorite,
  field,
  inputs,
  samples,
  showPreview,
  inputsReady,
}: {
  preset: FertPreset;
  onPick: (p: FertPreset) => void;
  onCustomize?: (p: FertPreset) => void;
  disabled?: boolean;
  favorite?: boolean;
  field: DashboardField;
  inputs: PresetInputs;
  samples: SoilSamplePoint[];
  showPreview: boolean;
  inputsReady: boolean;
}) {
  const uiMode = useUiMode((s) => s.mode);
  // AutoRx: resolve the preset's Rx surface (served build-time layer or client-computed) + its field average
  const { layer, avg, unit, loading, error } = useAutoRxLayer(
    field,
    preset,
    inputs,
    inputsReady,
    showPreview,
  );
  // `inputsReady` gates the Haney badge: `loadingData` covers the Haney fetch, so without it an empty
  // in-flight array would flash "Requires Haney Test" on a field that has it.
  const avgDigits = unit === "t/ac" ? 2 : 0;
  return (
    <div className="flex gap-2">
      <div
        role="button"
        tabIndex={0}
        aria-label={preset.label}
        onClick={() => {
          if (!disabled) onPick(preset);
        }}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled) {
            e.preventDefault();
            onPick(preset);
          }
        }}
        className={`relative isolate flex h-[190px] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-line focus:outline-none focus:ring-2 focus:ring-accent ${disabled ? "opacity-50" : "cursor-pointer"}`}
        title={`Build: ${preset.label}`}
      >
        <div className="absolute left-2 top-2 z-scrim rounded bg-raised/90 px-2 py-0.5 text-[11px] font-semibold text-fg shadow">
          {preset.label}
        </div>
        {favorite && (
          <div
            className="absolute left-2 top-[26px] z-scrim flex items-center gap-1 rounded bg-sky-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow"
            title="Our Favorite — the approach we recommend"
          >
            <SquareCheckBig size={11} /> Our Favorite
          </div>
        )}
        {/*
 AutoRx preview: the default Rx surface for this preset (real samples on top). Placeholder until the
            field is mapped from real/uploaded samples.
*/}
        {showPreview ? (
          // key on the field so a change of first-selected field remounts the map fresh (new camera
          // no stale boundary/points/choropleth from the previous field).
          <BuildRxPreviewMap
            key={field.field_id}
            field={field}
            layer={layer}
            samples={samples}
            loading={loading}
            error={error}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center bg-hover/20 px-3 text-center text-[11px] text-faint">
            Map with real soil samples to see the AutoRx preview
          </div>
        )}
        {/* Gated on the preset's own `pMin` flag, so it can never appear on a preset that does not use the credit. */}
        {preset.flags.pMin &&
          showPreview &&
          inputsReady &&
          (inputs?.haneyPoints?.length ?? 0) === 0 && (
            <span
              className="absolute right-2 top-2 z-scrim rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow"
              title="This preset's soil-biology credit needs Haney test results. Without them it computes the same rec as the preset without the modifier."
            >
              Requires Haney Test
            </span>
          )}
        {/*
 Customize -> the in-map Rx editor. Markup copied VERBATIM from the Map Soil card's button
            (PreviewMap.tsx) so the two read as one affordance; `stopPropagation` because the card body is
            itself a click target that runs the preset with its defaults.
*/}
        {onCustomize && showPreview && showsAdvancedControls(uiMode) && (
          <button
            className="absolute bottom-2 right-2 z-scrim flex items-center gap-1 rounded bg-accent px-2 py-1 text-[10px] font-medium text-canvas shadow hover:bg-accent-hover"
            onClick={(e) => {
              e.stopPropagation();
              onCustomize(preset);
            }}
            title="Edit the targets before saving"
          >
            <Settings2 size={11} /> Customize
          </button>
        )}
      </div>
      <div className="flex h-[190px] w-44 shrink-0 flex-col overflow-auto rounded-lg border border-line bg-raised/50 p-2 text-[11px]">
        {showPreview && (
          <div className="mb-1 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
            {loading
              ? "Field avg: …"
              : avg != null
                ? `Field avg: ${avg.toFixed(avgDigits)} ${unit}`
                : "Field avg: —"}
          </div>
        )}
        {preset.compareTo && (
          <div className="mb-1 text-[10px] font-semibold text-accent">
            Compare to: {preset.compareTo}
          </div>
        )}
        <div className="whitespace-pre-line text-muted">{preset.desc}</div>
        {preset.details && preset.details.length > 0 && (
          <ul className="mt-1.5 space-y-0.5 text-faint">
            {preset.details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
        {/* THE ROTATION NOTE — derived from the removal flag rather than a per-preset string or an ID list, so it appears exactly where it is TRUE: every preset that carries crop removal defaults to the corn+soybean two-season rotation. Albrecht K has no `replK` (pure saturation target, no maintenance), so it is excluded by the data rather than by hand, and a future preset gaining removal picks the note up automatically. */}
        {(preset.flags.replP || preset.flags.replK) && (
          <div className="mt-1.5 border-t border-line pt-1 text-[10px] italic text-faint">
            Default is calibrated for a 2-year spread on a corn/soy rotation
          </div>
        )}
      </div>
    </div>
  );
}
